import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle, useMemo } from 'react';
import { GoogleMap, useJsApiLoader, Marker, Polyline } from '@react-google-maps/api';
import { MapLocation, RouteInfo } from '../types';
import { useLanguage } from '../contexts/LanguageContext';

// Define libraries array outside component to prevent unnecessary re-renders
const libraries: ("places" | "geometry")[] = ["places", "geometry"];

interface GoogleMapsComponentProps {
  origin: MapLocation | null;
  destination: MapLocation | null;
  additionalDestinations?: MapLocation[];
  additionalDestinationAddresses?: string[];
  originAddress?: string;
  destinationAddress?: string;
  onRouteCalculated?: (routeInfo: RouteInfo) => void;
  style?: React.CSSProperties;
  className?: string;
}

export interface GoogleMapsComponentRef {
  calculateRoute: () => void;
  resetMap: () => void;
  fitMapToMarkers: () => void;
}

const GoogleMapsComponent = forwardRef<GoogleMapsComponentRef, GoogleMapsComponentProps>(({
  origin,
  destination,
  additionalDestinations = [],
  additionalDestinationAddresses = [],
  originAddress = '',
  destinationAddress = '',
  onRouteCalculated,
  style = { width: '100%', height: '400px' },
  className = ''
}, ref) => {
  // Get Google Maps API key from environment
  const googleMapsApiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || "";
  const { t } = useLanguage();
  
  // Load Google Maps API with places and geometry libraries
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey,
    libraries
  });

  // State for map and route
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [directionsService, setDirectionsService] = useState<google.maps.DirectionsService | null>(null);
  const [directionsRenderer, setDirectionsRenderer] = useState<google.maps.DirectionsRenderer | null>(null);
  const [originMarker, setOriginMarker] = useState<google.maps.Marker | null>(null);
  const [destinationMarker, setDestinationMarker] = useState<google.maps.Marker | null>(null);
  const [additionalMarkers, setAdditionalMarkers] = useState<google.maps.Marker[]>([]);
  const [calculating, setCalculating] = useState(false);
  const [routeCalculated, setRouteCalculated] = useState(false);
  
  // Default center (Mexico City)
  const defaultCenter = { lat: 19.4326, lng: -99.1332 };
  
  // Memoize map options to ensure google object is available
  const mapOptions = useMemo(() => {
    if (!isLoaded || !window.google || !window.google.maps || !window.google.maps.ControlPosition) return {};
    
    return {
      streetViewControl: false,
      mapTypeControl: true,
      mapTypeControlOptions: {
        position: google.maps.ControlPosition.TOP_RIGHT
      },
      zoomControl: true,
      zoomControlOptions: {
        position: google.maps.ControlPosition.RIGHT_BOTTOM
      },
      fullscreenControl: true,
      fullscreenControlOptions: {
        position: google.maps.ControlPosition.RIGHT_TOP
      }
    };
  }, [isLoaded]);
  
  // Clear all markers and route
  const clearMapElements = useCallback(() => {
    console.log('Clearing all map elements...');
    
    // Clear origin marker
    if (originMarker) {
      originMarker.setMap(null);
      setOriginMarker(null);
    }
    
    // Clear destination marker
    if (destinationMarker) {
      destinationMarker.setMap(null);
      setDestinationMarker(null);
    }
    
    // Clear additional markers
    additionalMarkers.forEach(marker => {
      marker.setMap(null);
    });
    setAdditionalMarkers([]);
    
    // Clear directions completely
    if (directionsRenderer) {
      // First clear the directions data
      directionsRenderer.setDirections({ routes: [] });
      // Then remove from map
      directionsRenderer.setMap(null);
    }
    
    setRouteCalculated(false);
    console.log('Map elements cleared');
  }, [originMarker, destinationMarker, additionalMarkers, directionsRenderer]);

  // Create or update markers based on current locations
  const updateMarkers = useCallback(() => {
    if (!map) return;
    
    console.log('Updating markers...', { origin, destination, additionalDestinations });
    
    // Clear existing markers first
    if (originMarker) {
      originMarker.setMap(null);
    }
    if (destinationMarker) {
      destinationMarker.setMap(null);
    }
    additionalMarkers.forEach(marker => marker.setMap(null));
    
    // Create origin marker if location exists
    if (origin) {
      console.log('Creating origin marker at:', origin);
      const newOriginMarker = new google.maps.Marker({
        position: origin,
        map,
        title: t('mapComponent.originMarker'),
        label: 'A',
        draggable: true,
        icon: {
          url: 'https://maps.google.com/mapfiles/ms/icons/green-dot.png',
          scaledSize: new google.maps.Size(40, 40)
        }
      });
      
      // Add drag listener to origin marker
      newOriginMarker.addListener('dragend', () => {
        const newPosition = newOriginMarker.getPosition();
        if (newPosition && onRouteCalculated) {
          const lat = newPosition.lat();
          const lng = newPosition.lng();
          console.log('Origin marker dragged to:', lat, lng);
          
          // Update route info with new origin
          onRouteCalculated({
            origin: { lat, lng },
            destination,
            distance: 0, // Will be recalculated
            duration: '',
            originAddress: `Lat: ${lat.toFixed(6)}, Lng: ${lng.toFixed(6)}`,
            destinationAddress
          });
        }
      });
      
      setOriginMarker(newOriginMarker);
    }
    
    const newAdditionalMarkers: google.maps.Marker[] = [];

    if (additionalDestinations.length > 0) {
      if (destination) {
        console.log('Creating waypoint marker (B) for destination at:', destination);
        const waypointMarker = new google.maps.Marker({
          position: destination,
          map,
          title: `${t('mapComponent.waypointMarker')} B`,
          label: 'B',
          draggable: true,
          icon: {
            url: 'https://maps.google.com/mapfiles/ms/icons/blue-dot.png',
            scaledSize: new google.maps.Size(40, 40)
          }
        });

        waypointMarker.addListener('dragend', () => {
          const newPosition = waypointMarker.getPosition();
          if (newPosition && onRouteCalculated) {
            const lat = newPosition.lat();
            const lng = newPosition.lng();
            console.log('Waypoint marker B dragged to:', lat, lng);

            onRouteCalculated({
              origin,
              destination: { lat, lng },
              distance: 0,
              duration: '',
              originAddress,
              destinationAddress: `Lat: ${lat.toFixed(6)}, Lng: ${lng.toFixed(6)}`
            });
          }
        });

        newAdditionalMarkers.push(waypointMarker);
      }

      additionalDestinations.forEach((location, index) => {
        if (location) {
          const isLastDestination = index === additionalDestinations.length - 1;
          const letter = String.fromCharCode(67 + index);

          console.log(`Creating ${isLastDestination ? 'final destination' : 'waypoint'} marker ${letter} at:`, location);

          const marker = new google.maps.Marker({
            position: location,
            map,
            title: isLastDestination
              ? t('mapComponent.destinationMarker')
              : `${t('mapComponent.waypointMarker')} ${letter}`,
            label: letter,
            draggable: true,
            icon: {
              url: isLastDestination
                ? 'https://maps.google.com/mapfiles/ms/icons/red-dot.png'
                : 'https://maps.google.com/mapfiles/ms/icons/blue-dot.png',
              scaledSize: new google.maps.Size(40, 40)
            }
          });

          newAdditionalMarkers.push(marker);
        }
      });
    } else {
      if (destination) {
        console.log('Creating destination marker at:', destination);
        const newDestinationMarker = new google.maps.Marker({
          position: destination,
          map,
          title: t('mapComponent.destinationMarker'),
          label: 'B',
          draggable: true,
          icon: {
            url: 'https://maps.google.com/mapfiles/ms/icons/red-dot.png',
            scaledSize: new google.maps.Size(40, 40)
          }
        });

        newDestinationMarker.addListener('dragend', () => {
          const newPosition = newDestinationMarker.getPosition();
          if (newPosition && onRouteCalculated) {
            const lat = newPosition.lat();
            const lng = newPosition.lng();
            console.log('Destination marker dragged to:', lat, lng);

            onRouteCalculated({
              origin,
              destination: { lat, lng },
              distance: 0,
              duration: '',
              originAddress,
              destinationAddress: `Lat: ${lat.toFixed(6)}, Lng: ${lng.toFixed(6)}`
            });
          }
        });

        setDestinationMarker(newDestinationMarker);
      }
    }

    setAdditionalMarkers(newAdditionalMarkers);
    
    console.log('Markers updated successfully');
  }, [map, origin, destination, additionalDestinations, onRouteCalculated, originAddress, destinationAddress, t]);

  // Calculate route between all points
  const calculateRoute = useCallback(async () => {
    if (!map || !directionsService || !origin || !destination) {
      console.warn('Cannot calculate route: missing required components or locations');
      return;
    }

    console.log('Starting route calculation...');
    setCalculating(true);

    try {
      let finalOrigin = origin;
      let finalDestination = destination;
      let waypoints: google.maps.DirectionsWaypoint[] = [];

      if (additionalDestinations.length > 0) {
        console.log('Route with additional destinations detected. Reorganizing waypoints for sequential route...');

        waypoints.push({
          location: new google.maps.LatLng(destination.lat, destination.lng),
          stopover: true
        });

        additionalDestinations.forEach((location, index) => {
          if (index < additionalDestinations.length - 1) {
            waypoints.push({
              location: new google.maps.LatLng(location.lat, location.lng),
              stopover: true
            });
          } else {
            finalDestination = location;
          }
        });

        console.log(`Sequential route: Origin -> ${waypoints.length} waypoint(s) -> Final Destination`);
      } else {
        console.log('Simple route: Origin -> Destination (no waypoints)');
      }

      console.log('Calculating route with waypoints:', waypoints.length);

      const request: google.maps.DirectionsRequest = {
        origin: new google.maps.LatLng(finalOrigin.lat, finalOrigin.lng),
        destination: new google.maps.LatLng(finalDestination.lat, finalDestination.lng),
        waypoints,
        travelMode: google.maps.TravelMode.DRIVING,
        unitSystem: google.maps.UnitSystem.METRIC,
        avoidHighways: false,
        avoidTolls: false
      };
      
      const result = await directionsService.route(request);
      
      if (result.routes && result.routes.length > 0) {
        const route = result.routes[0];
        const leg = route.legs[0];
        
        console.log('Route calculated successfully');
        console.log('Route details:', {
          distance: leg.distance?.value || 0,
          duration: leg.duration?.text || '',
          legs: route.legs.length
        });
        
        // Display route on map
        if (directionsRenderer) {
          directionsRenderer.setDirections(result);
          directionsRenderer.setMap(map);
        }
        
        // Calculate total distance and prepare segment information
        let totalDistance = 0;
        const segmentDistances: number[] = [];
        const segmentDurations: string[] = [];
        
        route.legs.forEach((routeLeg) => {
          const legDistance = (routeLeg.distance?.value || 0) / 1000; // Convert to km
          totalDistance += legDistance;
          segmentDistances.push(legDistance);
          segmentDurations.push(routeLeg.duration?.text || '');
        });
        
        // Create route info object
        const routeInfo: RouteInfo = {
          origin,
          destination,
          distance: totalDistance,
          duration: route.legs.reduce((total, routeLeg) => {
            const duration = routeLeg.duration?.value || 0;
            return total + duration;
          }, 0) > 3600 
            ? `${Math.floor(route.legs.reduce((total, routeLeg) => total + (routeLeg.duration?.value || 0), 0) / 3600)} ${t('common.hours')} ${Math.floor((route.legs.reduce((total, routeLeg) => total + (routeLeg.duration?.value || 0), 0) % 3600) / 60)} ${t('common.minutes')}`
            : `${Math.floor(route.legs.reduce((total, routeLeg) => total + (routeLeg.duration?.value || 0), 0) / 60)} ${t('common.minutes')}`,
          originAddress,
          destinationAddress,
          isMultiSegmentRoute: additionalDestinations.length > 0,
          segmentDistances: segmentDistances.length > 1 ? segmentDistances : undefined,
          segmentDurations: segmentDurations.length > 1 ? segmentDurations : undefined,
          encodedPolyline: route.overview_polyline
        };
        
        console.log('Route info prepared:', routeInfo);
        
        // Call the callback with route information
        if (onRouteCalculated) {
          onRouteCalculated(routeInfo);
        }
        
        setRouteCalculated(true);
        
        // Fit map to show the entire route
        const bounds = new google.maps.LatLngBounds();
        route.legs.forEach(routeLeg => {
          bounds.extend(routeLeg.start_location);
          bounds.extend(routeLeg.end_location);
        });
        map.fitBounds(bounds);
        
      } else {
        console.error('No routes found in the response');
        throw new Error(t('mapComponent.routeNotFound'));
      }
      
    } catch (error) {
      console.error('Error calculating route:', error);
      
      if (onRouteCalculated) {
        onRouteCalculated({
          origin: null,
          destination: null,
          distance: 0,
          duration: '',
          originAddress: '',
          destinationAddress: ''
        });
      }
    } finally {
      setCalculating(false);
    }
  }, [map, directionsService, origin, destination, additionalDestinations, onRouteCalculated, originAddress, destinationAddress, t]);

  // Fit map to show all markers
  const fitMapToMarkers = useCallback(() => {
    if (!map) return;
    
    const bounds = new google.maps.LatLngBounds();
    let hasMarkers = false;
    
    if (origin) {
      bounds.extend(origin);
      hasMarkers = true;
    }
    
    if (destination) {
      bounds.extend(destination);
      hasMarkers = true;
    }
    
    additionalDestinations.forEach(location => {
      if (location) {
        bounds.extend(location);
        hasMarkers = true;
      }
    });
    
    if (hasMarkers) {
      map.fitBounds(bounds);
      
      // Ensure minimum zoom level
      const listener = google.maps.event.addListener(map, 'bounds_changed', () => {
        if (map.getZoom() && map.getZoom()! > 15) {
          map.setZoom(15);
        }
        google.maps.event.removeListener(listener);
      });
    }
  }, [map, origin, destination, additionalDestinations]);

  // Reset map to default state
  const resetMap = useCallback(() => {
    console.log('Resetting map to default state...');
    
    clearMapElements();
    
    if (map) {
      map.setCenter(defaultCenter);
      map.setZoom(6);
    }
    
    // Clear route info
    if (onRouteCalculated) {
      onRouteCalculated({
        origin: null,
        destination: null,
        distance: 0,
        duration: '',
        originAddress: '',
        destinationAddress: ''
      });
    }
    
    console.log('Map reset completed');
  }, [clearMapElements, map, onRouteCalculated]);

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    calculateRoute,
    resetMap,
    fitMapToMarkers
  }));

  // Initialize directions service and renderer when map loads
 const onMapLoad = useCallback((mapInstance: google.maps.Map) => {
  console.log('Map loaded successfully');
  setMap(mapInstance);
  
  // Establecer centro y zoom iniciales explícitamente
  mapInstance.setCenter(defaultCenter);
  mapInstance.setZoom(6);
  
  // Disparar un evento de redimensionamiento para asegurar que el mapa se renderice correctamente
  // Esto es a menudo necesario si el contenedor del mapa cambia de tamaño o está inicialmente oculto
  google.maps.event.trigger(mapInstance, 'resize'); // <--- Añade esta línea
  
  // Inicializar el servicio de direcciones y el renderizador
  const service = new google.maps.DirectionsService();
  const renderer = new google.maps.DirectionsRenderer({
    suppressMarkers: true, // Usaremos marcadores personalizados
    polylineOptions: {
      strokeColor: '#0096FF',
      strokeWeight: 5,
      strokeOpacity: 0.8
    }
  });
  
  setDirectionsService(service);
  setDirectionsRenderer(renderer);
  
  console.log('Directions service and renderer initialized');
}, []);

  // Update markers when locations change
  useEffect(() => {
    if (map && isLoaded) {
      updateMarkers();
    }
  }, [map, isLoaded, updateMarkers]);

  // Auto-fit map when markers are updated
  useEffect(() => {
    if (map && (origin || destination || additionalDestinations.length > 0)) {
      // Small delay to ensure markers are rendered
      setTimeout(() => {
        fitMapToMarkers();
      }, 100);
    }
  }, [origin, destination, additionalDestinations, fitMapToMarkers, map]);

  // Handle map click to place markers
  const handleMapClick = useCallback((event: google.maps.MapMouseEvent) => {
    if (!event.latLng || !onRouteCalculated) return;
    
    const lat = event.latLng.lat();
    const lng = event.latLng.lng();
    const clickedLocation = { lat, lng };
    
    console.log('Map clicked at:', lat, lng);
    
    if (!origin) {
      // Set as origin
      console.log('Setting clicked location as origin');
      onRouteCalculated({
        origin: clickedLocation,
        destination,
        distance: 0,
        duration: '',
        originAddress: `Lat: ${lat.toFixed(6)}, Lng: ${lng.toFixed(6)}`,
        destinationAddress
      });
    } else if (!destination) {
      // Set as destination
      console.log('Setting clicked location as destination');
      onRouteCalculated({
        origin,
        destination: clickedLocation,
        distance: 0,
        duration: '',
        originAddress,
        destinationAddress: `Lat: ${lat.toFixed(6)}, Lng: ${lng.toFixed(6)}`
      });
    }
  }, [origin, destination, onRouteCalculated, originAddress, destinationAddress]);

  if (loadError) {
    return (
      <div className="flex items-center justify-center h-96 bg-gray-100 rounded-lg">
        <div className="text-center">
          <p className="text-red-600 font-medium">{t('mapComponent.errorLoading')}</p>
          <p className="text-gray-500 text-sm mt-1">Google Maps API</p>
        </div>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="flex items-center justify-center h-96 bg-gray-100 rounded-lg">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary mx-auto mb-2"></div>
          <p className="text-gray-600">{t('mapComponent.loading')}</p>
        </div>
      </div>
    );
  }

  // Additional check to ensure Google Maps API is fully available
  if (!window.google || !window.google.maps || !window.google.maps.Map) {
    return (
      <div className="flex items-center justify-center h-96 bg-gray-100 rounded-lg">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary mx-auto mb-2"></div>
          <p className="text-gray-600">{t('mapComponent.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className={`relative ${className}`} 
      style={style}
      data-map-container
    >
      <GoogleMap
        mapContainerStyle={{ width: '100%', height: '100%' }}
        center={map ? undefined : defaultCenter}
        zoom={map ? undefined : 6}
        onLoad={onMapLoad}
        onClick={handleMapClick}
        options={mapOptions}
      >
        {/* Markers are handled programmatically for better control */}
      </GoogleMap>

      {/* Status indicator in top right */}
      {calculating && (
        <div className="absolute top-3 right-3 bg-white px-3 py-2 rounded-md shadow-md flex items-center z-40">
          <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-primary mr-2"></div>
          <span className="text-sm text-gray-700">{t('mapComponent.calculating')}</span>
        </div>
      )}
    </div>
  );
});

GoogleMapsComponent.displayName = 'GoogleMapsComponent';

export default GoogleMapsComponent;